<template>
  <div class="pos-ticket-page">
    <div class="pos-ticket-toolbar no-print">
      <button type="button" class="btn-ticket btn-print" @click="printTicket(false)">
        <i class="fas fa-print me-1"></i> Imprimir
      </button>
      <button type="button" class="btn-ticket btn-print-evidence" @click="printTicket(true)">
        <i class="fas fa-image me-1"></i> Imprimir con evidencias
      </button>
      <button type="button" class="btn-ticket btn-close-tab" @click="closeTicket">
        <i class="fas fa-times me-1"></i> Cerrar
      </button>
    </div>

    <div v-if="loading" class="pos-ticket-empty no-print">
      <p>Cargando ticket...</p>
    </div>

    <div v-else-if="!ticket" class="pos-ticket-empty no-print">
      <p>No hay datos de ticket para mostrar.</p>
      <button type="button" class="btn-ticket btn-close-tab" @click="closeTicket">Cerrar</button>
    </div>

    <div v-else class="ticket-layout" id="pos-ticket-print">
      <article class="ticket">
        <div v-if="logoUrl" class="ticket-logo-wrap">
          <img :src="logoUrl" alt="Logo empresa" class="ticket-logo" />
        </div>
        <header class="ticket-header">
          <h1 class="ticket-company">{{ companyLine }}</h1>
          <p v-if="ticket.company?.nit" class="ticket-sub">NIT: {{ ticket.company.nit }}</p>
          <p class="ticket-sub">Tel.: {{ ticket.company?.phone || "—" }}</p>
        </header>

        <section class="ticket-doc">
          <div class="ticket-sep" style="width: 100%;border-style: dashed;border-width: 0.2px;"></div>
          <div class="ticket-doc-title">Orden de pedido</div>
          <div class="ticket-doc-row">
            <span class="ticket-doc-number">No. {{ ticket.invoiceNo }}</span>
            <span class="ticket-doc-date">{{ ticket.dateFormatted }}</span>
          </div>
        </section>

        <section class="ticket-customer">
          <div class="ticket-customer-name">{{ customerName }}</div>
          <div class="ticket-customer-doc">{{ documentLine }}</div>
          <div v-if="phoneLine" class="ticket-customer-phone">{{ phoneLine }}</div>
          <div v-if="locationLine" class="ticket-customer-address">{{ locationLine }}</div>
          <div v-if="addressLine" class="ticket-customer-address">{{ addressLine }}</div>
        </section>

        <section class="ticket-items">
          <div class="ticket-items-head">
            <div class="th-qty">CANT</div>
            <div class="th-ref">REF</div>
            <div class="th-color">COLOR</div>
            <div class="th-size">TALLA</div>
            <div class="th-total">
              <span>VALOR</span>
            </div>
          </div>
          <div class="ticket-sep" style="width: 100%;border-style: dashed;border-width: 0.2px;"></div>
          <div
            v-for="(item, index) in ticket.items"
            :key="index"
            class="ticket-item-row"
          >
            <div class="td-qty">{{ formatQty(item.quantity) }}</div>
            <div class="td-ref">{{ item.ref || "—" }}</div>
            <div class="td-color">{{ item.color || "—" }}</div>
            <div class="td-size">{{ item.talla || "—" }}</div>
            <div class="td-total">{{ money(item.total) }}</div>
          </div>
        </section>

        <section class="ticket-totals">
          <div class="ticket-totals-block">
            <div class="ticket-total-line ticket-total-main">
              <span class="ticket-total-label">SUBTOTAL ===========&gt;</span>
              <span class="ticket-total-value">{{ money(ticket.subtotal) }}</span>
            </div>
            <div v-if="ticket.discountAmount > 0" class="ticket-total-line">
              <span class="ticket-total-label">DESCUENTO ==&gt;</span>
              <span class="ticket-total-value">−{{ money(ticket.discountAmount) }}</span>
            </div>
            <div v-if="ticket.flete > 0" class="ticket-total-line">
              <span class="ticket-total-label">FLETE ==&gt;</span>
              <span class="ticket-total-value">+{{ money(ticket.flete) }}</span>
            </div>
            <div v-if="ticket.giftValue > 0" class="ticket-total-line">
              <span class="ticket-total-label">OBSEQUIO ==&gt;</span>
              <span class="ticket-total-value">+{{ money(ticket.giftValue) }}</span>
            </div>
            <div class="ticket-total-line ticket-total-main">
              <span class="ticket-total-label">TOTAL ===========&gt;</span>
              <span class="ticket-total-value">{{ money(ticket.total) }}</span>
            </div>
            <div class="ticket-total-line">
              <span class="ticket-total-label">RECIBIDO ==&gt;</span>
              <span class="ticket-total-value">{{ money(ticket.received) }}</span>
            </div>
            <div v-if="ticket.isFullyPaid" class="ticket-total-line">
              <span class="ticket-total-label">CAMBIO ==&gt;</span>
              <span class="ticket-total-value">{{ money(ticket.change) }}</span>
            </div>
            <div v-else class="ticket-total-line ticket-total-pending">
              <span class="ticket-total-label">PENDIENTE ==&gt;</span>
              <span class="ticket-total-value">{{ money(ticket.pending) }}</span>
            </div>
          </div>
        </section>

        <section class="ticket-payment">
          <div class="ticket-payment-head">
            <span>Forma de pago</span>
            <span>Valor</span>
          </div>
          <div
            v-for="(p, idx) in paymentRows"
            :key="idx"
            class="ticket-payment-row"
          >
            <span>{{ p.method }}</span>
            <span>{{ money(p.amount) }}</span>
          </div>
        </section>

        <section class="ticket-footer">
          <div class="ticket-seller">VENDEDOR</div>
          <div class="ticket-footer-block">
            <div class="ticket-footer-line">
              <span>TOTAL :</span>
              <span>{{ money(ticket.total) }}</span>
            </div>
            <div class="ticket-footer-line">
              <span>NRO. ARTICULOS VEND. :</span>
              <span>{{ ticket.articlesCount }}</span>
            </div>
            <div class="ticket-footer-line">
              <span>NRO. ITEMS FACT. :</span>
              <span>{{ ticket.itemsCount }}</span>
            </div>
            <div class="ticket-footer-line">
              <span>USUARIO :</span>
              <span>{{ ticket.seller }}</span>
            </div>
            <div class="ticket-footer-line">
              <span>FECHA :</span>
              <span>{{ ticket.dateFormatted }}</span>
            </div>
          </div>
        </section>
      </article>

      <!-- Panel de evidencias de pago -->
      <aside v-if="evidenceImages.length > 0" class="ticket-evidence-panel" :class="{ 'evidence-hidden-print': !printWithEvidence }">
        <h3 class="ticket-evidence-title">Evidencias de pago</h3>
        <div v-for="(img, idx) in evidenceImages" :key="idx" class="ticket-evidence-item">
          <img :src="img.url" :alt="'Evidencia ' + (idx + 1)" class="ticket-evidence-img" />
          <p class="ticket-evidence-method">{{ img.method }}</p>
          <p v-if="img.amount > 0" class="ticket-evidence-amount">{{ money(img.amount) }}</p>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import {
  loadPosTicketData,
  formatTicketMoneyWithSymbol,
  formatTicketDate,
  buildInvoiceNumber,
  mergeTicketCompany,
  resolveCompanyLogoUrl,
} from "@/utils/posTicketPrint";
import settingsService from "@/services/settings.service";
import remissionsService from "@/services/remissions.service";
import mediaService from "@/services/media.service";

const body = document.getElementsByTagName("body")[0];

export default {
  name: "PosTicket",
  data() {
    return {
      ticket: null,
      loading: true,
      printWithEvidence: false,
      mediaDocs: [],
    };
  },
  computed: {
    companyLine() {
      const name = this.ticket?.company?.company_name || "MI STOCK";
      return String(name).toUpperCase();
    },
    customerName() {
      return String(this.ticket?.client?.name || "CONSUMIDOR FINAL").toUpperCase();
    },
    documentLine() {
      const type = this.ticket?.client?.document_type || "CC";
      const doc = this.ticket?.client?.document || "—";
      return `${type} No. : ${doc}`;
    },
    phoneLine() {
      const phone = this.ticket?.client?.phone;
      if (!phone) return "";
      return `Tel.: ${phone}`;
    },
    locationLine() {
      const parts = [this.ticket?.client?.province, this.ticket?.client?.city].filter(Boolean);
      if (!parts.length) return "";
      return parts.join(" / ");
    },
    addressLine() {
      const address = this.ticket?.client?.address;
      if (!address) return "";
      return `Dirección: ${address}`;
    },
    logoUrl() {
      return resolveCompanyLogoUrl(this.ticket?.company);
    },
    paymentRows() {
      if (!this.ticket) return [];
      // Si hay pagos reales registrados, listarlos individualmente
      if (Array.isArray(this.ticket.payments) && this.ticket.payments.length > 0) {
        return this.ticket.payments.map((p) => ({
          method: String(p.payment_method || "EFECTIVO").toUpperCase(),
          amount: Number(p.amount || 0),
        }));
      }
      // Si no hay pagos registrados pero se recibió monto, mostrar una sola fila
      if (Number(this.ticket.received) > 0) {
        return [{ method: String(this.ticket.paymentMethod || "EFECTIVO").toUpperCase(), amount: Number(this.ticket.received) }];
      }
      return [];
    },
    evidenceImages() {
      const base = process.env.VUE_APP_API_IMAGE_URL || '';
      const images = [];

      // Evidencias de pago (con evidence_path)
      if (this.ticket && Array.isArray(this.ticket.payments)) {
        this.ticket.payments
          .filter(p => p.evidence_path)
          .forEach(p => {
            images.push({
              url: base + encodeURI(p.evidence_path),
              method: String(p.payment_method || "EFECTIVO").toUpperCase(),
              amount: Number(p.amount || 0),
            });
          });
      }

      // Documentos media (imágenes subidas como evidencias)
      if (Array.isArray(this.mediaDocs)) {
        this.mediaDocs
          .filter(d => {
            const ext = (d.file_path || '').split('.').pop().toLowerCase();
            return ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext);
          })
          .forEach(d => {
            images.push({
              url: base + encodeURI(d.file_path),
              method: 'Evidencia',
              amount: 0,
            });
          });
      }

      return images;
    },
  },
  methods: {
    money(value) {
      return formatTicketMoneyWithSymbol(value);
    },
    formatQty(qty) {
      const n = Number(qty) || 0;
      return Number.isInteger(n) ? String(n) : n.toLocaleString("es-CO", { maximumFractionDigits: 2 });
    },
    itemLabel(item) {
      const name = String(item.name || "").toUpperCase();
      const suffix = item.ref ? ` (${item.ref})` : " (Und)";
      return name + suffix;
    },
    printTicket(withEvidence = false) {
      this.printWithEvidence = withEvidence;
      if (withEvidence) {
        document.body.classList.add('print-with-evidence');
      } else {
        document.body.classList.remove('print-with-evidence');
      }
      this.$nextTick(() => {
        window.print();
        // Limpiar después de imprimir
        document.body.classList.remove('print-with-evidence');
      });
    },
    closeTicket() {
      window.close();
      if (!window.closed) {
        this.$router.push({ name: "POS" });
      }
    },
    async fetchCompanySettings() {
      try {
        const settingsRes = await settingsService.get();
        return settingsRes.data || {};
      } catch (e) {
        console.error("No se pudo cargar configuración:", e);
        return {};
      }
    },
    async loadMediaDocs(remissionId) {
      try {
        const res = await mediaService.getData(remissionId);
        this.mediaDocs = (res.data || []).filter(item => item.mediaable_type === 'remission');
      } catch (e) {
        console.error("Error al cargar documentos media:", e);
        this.mediaDocs = [];
      }
    },
    buildTicketFromRemission(r, company) {
      const items = (r.details || []).map((d) => ({
        name: String(d.inventory?.name || "PRODUCTO").toUpperCase(),
        ref: d.inventory?.ref || null,
        color: d.variant?.color?.name || "",
        talla: d.variant?.size?.size || "",
        quantity: d.quantity,
        unitPrice: d.unit_price,
        total: d.total,
      }));
      const articlesCount = items.reduce((sum, i) => sum + Number(i.quantity || 0), 0);
      const total = Number(r.grand_total || r.total || 0);
      // Calcular el total pagado a partir de los pagos reales (preferido) o amount_paid
      const paymentsTotal = Array.isArray(r.payments)
        ? r.payments.reduce((sum, p) => sum + Number(p.amount || 0), 0)
        : 0;
      const received = paymentsTotal > 0
        ? paymentsTotal
        : (Number(r.amount_paid) > 0 ? Number(r.amount_paid) : 0);
      const change = Math.max(0, received - total);
      const pending = Math.max(0, total - received);
      const isFullyPaid = received >= total;
      return mergeTicketCompany({
        remissionId: r.id,
        invoiceNo: buildInvoiceNumber(r.id),
        dateFormatted: formatTicketDate(r.created_at),
        client: {
          id: r.user?.id || null,
          name: r.user?.name || r.name || "CONSUMIDOR FINAL",
          document_type: r.user?.document_type || "CC",
          document: r.user?.document || r.document || String(r.user?.id || ""),
          phone: r.user?.phone || r.phone || "",
          address: r.user?.address || r.address || "",
          province: r.user?.province || "",
          city: r.user?.city || "",
        },
        items,
        subtotal: Number(r.subtotal || 0),
        itemsTotal: items.reduce((sum, i) => sum + Number(i.total || 0), 0),
        discountAmount: Number(r.discount_amount || 0),
        flete: Number(r.flete || 0),
        giftValue: Number(r.gift_value || 0),
        total,
        received,
        change,
        pending,
        isFullyPaid,
        payments: Array.isArray(r.payments) ? r.payments : [],
        paymentMethod: r.payment_method || "EFECTIVO",
        articlesCount,
        itemsCount: items.length,
        seller: String(r.seller?.name || "LOCAL").toUpperCase(),
        company: company || {},
      }, company);
    },
    async loadTicket() {
      this.loading = true;
      const remissionId = this.$route.query.id;
      try {
        const company = await this.fetchCompanySettings();
        // La API es la fuente de verdad cuando hay remissionId: siempre consulta
        // primero la API para garantizar color/talla completos para cualquier usuario.
        if (remissionId) {
          try {
            const res = await remissionsService.getData(remissionId);
            this.ticket = this.buildTicketFromRemission(res.data, company);
            await this.loadMediaDocs(remissionId);
            return;
          } catch (apiErr) {
            console.warn("No se pudo cargar el ticket desde la API, usando cache local:", apiErr);
            // Fallback al cache solo si la API falla
            const cached = loadPosTicketData(remissionId);
            if (cached) {
              this.ticket = mergeTicketCompany(cached, company);
              await this.loadMediaDocs(remissionId);
              return;
            }
            this.ticket = null;
            return;
          }
        }
        // Sin remissionId: usar cache local (ticket sin remisión asociada)
        const cached = loadPosTicketData();
        if (cached) {
          this.ticket = mergeTicketCompany(cached, company);
          return;
        }
        this.ticket = null;
      } catch (e) {
        console.error("Error al cargar ticket:", e);
        this.ticket = null;
      } finally {
        this.loading = false;
      }
    },
  },
  async mounted() {
    this.$store.state.hideConfigButton = true;
    this.$store.state.showSidenav = false;
    this.$store.state.showNavbar = false;
    this.$store.state.showFooter = false;
    body.classList.remove("g-sidenav-show");
    await this.loadTicket();
  },
  beforeUnmount() {
    this.$store.state.hideConfigButton = false;
    this.$store.state.showSidenav = true;
    this.$store.state.showNavbar = true;
    this.$store.state.showFooter = true;
    body.classList.add("g-sidenav-show");
  },
};
</script>

<style scoped>
.pos-ticket-page {
  min-height: 100vh;
  background: #e9ecef;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.pos-ticket-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  width: 80mm;
  max-width: 100%;
  justify-content: center;
}

.btn-ticket {
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  color: #fff;
}

.btn-print {
  background: #82d616;
}

.btn-print-evidence {
  background: #4facfe;
}

.btn-close-tab {
  background: #67748e;
}

.pos-ticket-empty {
  text-align: center;
  padding: 2rem;
}

.ticket {
  width: 80mm;
  max-width: 100%;
  background: #fff;
  color: #000;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 12px;
  line-height: 1.35;
  padding: 3mm 2mm;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
}

.ticket-logo-wrap {
  text-align: center;
  margin-bottom: 0.5rem;
}

.ticket-logo {
  max-width: 56mm;
  max-height: 20mm;
  width: auto;
  height: auto;
  object-fit: contain;
}

.ticket-header {
  text-align: center;
  margin-bottom: 0.5rem;
}

.ticket-company {
  font-size: 12px;
  font-weight: 700;
  margin: 0 0 0.25rem;
  line-height: 1.25;
  text-transform: uppercase;
}

.ticket-sub {
  margin: 0;
  font-size: 12px;
}

.ticket-sep {
  text-align: center;
  letter-spacing: 0;
  margin: 0.35rem 0;
  font-size: 12px;
  overflow: hidden;
  white-space: nowrap;
}

.ticket-doc {
  text-align: center;
  margin-bottom: 0.5rem;
}

.ticket-doc-title {
  font-weight: 700;
  font-size: 12px;
  text-transform: uppercase;
}

.ticket-doc-row {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  margin-top: 0.25rem;
}

.ticket-customer {
  margin-bottom: 0.5rem;
}

.ticket-customer-name {
  font-weight: 700;
  font-size: 12px;
  text-transform: uppercase;
}

.ticket-customer-doc {
  font-size: 12px;
}

.ticket-customer-phone {
  font-size: 12px;
}

.ticket-customer-address {
  font-size: 12px;
}

.ticket-items {
  margin-bottom: 0.5rem;
}

.ticket-items-head {
  display: flex;
  font-weight: 700;
  font-size: 12px;
  gap: 0.25rem;
}

.th-qty,
.td-qty {
  width: 12%;
  text-align: center;
}

.th-ref,
.td-ref {
  width: 20%;
  text-align: left;
}

.th-color,
.td-color {
  flex: 1;
  text-align: center;
}

.th-size,
.td-size {
  width: 15%;
  text-align: center;
}

.th-total,
.td-total {
  width: 28%;
  text-align: right;
}

.th-sub {
  display: block;
  font-size: 11px;
  font-weight: 400;
}

.ticket-item-row {
  display: flex;
  font-size: 10px;
  gap: 0.25rem;
  margin-bottom: 0.25rem;
}

.ticket-totals {
  margin-bottom: 0.5rem;
}

.ticket-total-line {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  margin-bottom: 0.15rem;
}

.ticket-total-main {
  font-weight: 700;
  margin-top: 0.25rem;
}

.ticket-total-pending {
  font-weight: 700;
  margin-top: 0.25rem;
}

.ticket-total-pending .ticket-total-value {
  font-weight: 700;
}

.ticket-payment {
  margin-bottom: 0.5rem;
}

.ticket-payment-head,
.ticket-payment-row {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
}

.ticket-payment-head {
  font-weight: 700;
}

.ticket-footer {
  margin-bottom: 0.5rem;
}

.ticket-seller {
  text-align: center;
  font-weight: 700;
  font-size: 12px;
  margin-bottom: 0.25rem;
}

.ticket-footer-block {
  border: 1px solid #000;
  padding: 0.35rem;
}

.ticket-footer-line {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  margin-bottom: 0.15rem;
}

.ticket-legal {
  text-align: center;
  font-size: 11px;
  margin-top: 0.5rem;
}

/* Layout con evidencias */
.ticket-layout {
  display: flex;
  gap: 4mm;
  align-items: flex-start;
  justify-content: center;
}

.ticket-evidence-panel {
  width: 100mm;
  background: #fff;
  padding: 3mm 3mm;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
}

.ticket-evidence-title {
  font-size: 13px;
  font-weight: 700;
  text-align: center;
  margin: 0 0 0.5rem;
  text-transform: uppercase;
  border-bottom: 1px dashed #000;
  padding-bottom: 0.25rem;
}

.ticket-evidence-item {
  margin-bottom: 0.5rem;
  text-align: center;
  page-break-inside: avoid;
}

.ticket-evidence-img {
  width: 100%;
  max-width: 94mm;
  height: auto;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-bottom: 0.15rem;
}

.ticket-evidence-method {
  font-size: 11px;
  font-weight: 700;
  margin: 0;
}

.ticket-evidence-amount {
  font-size: 11px;
  margin: 0;
}

@media print {
  .no-print {
    display: none !important;
  }

  .pos-ticket-page {
    background: #fff;
    padding: 0;
    align-items: stretch;
  }

  body.print-with-evidence .pos-ticket-page {
    align-items: center;
  }

  .ticket-layout {
    box-shadow: none;
    gap: 10mm;
    justify-content: center;
    width: 100%;
  }

  .ticket {
    box-shadow: none;
    width: 80mm;
    max-width: 100%;
  }

  .ticket-evidence-panel {
    box-shadow: none;
  }

  /* Ocultar evidencias si se imprime sin ellas */
  .evidence-hidden-print {
    display: none !important;
  }

  body:not(.print-with-evidence) {
    @page {
      size: letter portrait;
      margin: 0;
    }
  }

  body {
    background: #fff;
  }
}

/* Cuando se imprime con evidencias, la página es más ancha */
@media print {
  body.print-with-evidence {
    @page {
      size: letter portrait;
      margin: 0;
    }
  }
}
</style>

<style>
@media print {
  body.print-with-evidence {
    @page {
      size: letter portrait;
      margin: 15mm;
    }
  }
}
</style>
